/**
 * ProjectPoller
 *
 * Framework-agnostic polling state machine that repeatedly invokes a single
 * `step` until it reaches a terminal state (COMPLETED / ERROR) or the maximum
 * number of attempts is exceeded (TIMEOUT).
 *
 * Responsibilities are intentionally narrow:
 * - own the interval timer, attempt counting and timeout
 * - guarantee a single in-flight polling loop (single-flight)
 * - prevent overlapping `step` invocations (re-entrancy guard)
 *
 * It deliberately knows nothing about React, dispatch or the project domain.
 * Callers wire it to their state machine through the lifecycle callbacks.
 */

export type PollResult = "COMPLETED" | "ERROR" | "TIMEOUT";

export interface PollStepResult {
	status: "IN_PROGRESS" | "COMPLETED" | "ERROR";
	error?: string;
}

export interface ProjectPollerCallbacks {
	/** Performs a single polling tick and reports the current generation status. */
	step: () => Promise<PollStepResult>;
	/** Invoked once when polling starts. */
	onStart?: () => void;
	/** Invoked when the step reports COMPLETED. */
	onCompleted?: () => void;
	/** Invoked when the step reports ERROR or throws. */
	onError?: (message: string) => void;
	/** Invoked when the maximum number of attempts is exceeded. */
	onTimeout?: () => void;
}

export interface ProjectPollerOptions {
	intervalMs?: number;
	maxAttempts?: number;
}

const DEFAULT_INTERVAL_MS = 5_000;
const DEFAULT_MAX_ATTEMPTS = 36; // 36 × 5 s = 3 min

export class ProjectPoller {
	private inFlight: Promise<PollResult> | null = null;
	private readonly callbacks: ProjectPollerCallbacks;
	private readonly intervalMs: number;
	private readonly maxAttempts: number;

	constructor(
		callbacks: ProjectPollerCallbacks,
		options: ProjectPollerOptions = {}
	) {
		this.callbacks = callbacks;
		this.intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
		this.maxAttempts = options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
	}

	/** Whether a polling loop is currently running. */
	isRunning(): boolean {
		return this.inFlight !== null;
	}

	/**
	 * Starts the polling loop. If a loop is already running, the existing
	 * in-flight promise is returned (single-flight guarantee).
	 */
	start(): Promise<PollResult> {
		if (this.inFlight) {
			return this.inFlight;
		}

		this.inFlight = new Promise<PollResult>((resolve) => {
			let attempts = 0;
			let requestInFlight = false;

			this.callbacks.onStart?.();

			const finish = (result: PollResult) => {
				clearInterval(intervalId);
				this.inFlight = null;
				resolve(result);
			};

			const intervalId = setInterval(async () => {
				if (requestInFlight) {
					return;
				}
				requestInFlight = true;

				try {
					attempts += 1;

					if (attempts > this.maxAttempts) {
						this.callbacks.onTimeout?.();
						finish("TIMEOUT");
						return;
					}

					const outcome = await this.callbacks.step();

					if (outcome.status === "COMPLETED") {
						this.callbacks.onCompleted?.();
						finish("COMPLETED");
						return;
					}

					if (outcome.status === "ERROR") {
						this.callbacks.onError?.(
							outcome.error || "Unknown error during project generation"
						);
						finish("ERROR");
						return;
					}
				} catch (err) {
					const message = err instanceof Error ? err.message : "Polling failed";
					this.callbacks.onError?.(message);
					finish("ERROR");
					return;
				} finally {
					requestInFlight = false;
				}
			}, this.intervalMs);
		});

		return this.inFlight;
	}
}
